/**
 * @fileoverview Banner for the Documents migration tab — SAS configured vs not.
 * @module system-web/components/migration/DocumentsStorageBanner
 *
 * Responsibilities:
 * - Load storage-status through legacyMigrationService (never apiClient in the page).
 * - Show a real "Legacy storage not configured" empty-state when SAS is missing.
 *
 * Dependencies:
 * - lucide-react
 * - legacyMigrationService.fetchLegacyStorageStatus
 *
 * Architecture:
 * - Online exception. Status is held in component state, never Dexie.
 *
 * @component
 * Purpose: Empty / not-configured state for the Documents grid.
 * Props: none (self-contained status fetch)
 * Behavior: warns when SAS missing; silent when configured
 * Data Source: remote (system-api storage-status)
 * Constraints: never displays SAS or URLs
 * Performance: one fetch on mount
 */

import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { fetchLegacyStorageStatus } from '../../services/legacyMigrationService';

/**
 * @component DocumentsStorageBanner
 */
export function DocumentsStorageBanner(): JSX.Element | null {
  const [configured, setConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchLegacyStorageStatus()
      .then((s) => {
        if (!cancelled) setConfigured(s.configured);
      })
      .catch(() => {
        if (!cancelled) setConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (configured !== false) return null;

  return (
    <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-3 px-4 flex items-center gap-2 text-xs text-amber-800">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      <div>
        <span className="font-semibold text-amber-900">Legacy storage not configured. </span>
        <span>
          Documents metadata still applies without SAS (filenames linked on occupancy records — that is the n / total count).
          Blob health-checks and file re-host to Azure need LEGACY_STORAGE_SAS_URL on system-api / workers.
        </span>
      </div>
    </div>
  );
}
